import { useState, useEffect } from 'react';
import { Calendar, Clock, MapPin, Phone, AlertCircle, CheckCircle2, MessageCircle, User, Activity, Stethoscope, TestTube, ClipboardCheck, FileText, Home, Mail, Pill, Target, ChevronRight, CheckCircle, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Progress } from './ui/progress';
import { useNurseDashboard } from '../lib/hooks/useDashboards';
import { LoadingState } from './common/LoadingState';
import { AlertCard } from './common/AlertCard';
import { EmptyState } from './common/EmptyState';
import { useAppointments } from '@/lib/hooks/useAppointments';
import { useAuth } from '@/lib/hooks/useAuth';
import { toast } from 'sonner';

interface NurseDashboardProps { 
  onNavigate: (view: string) => void;
  onSelectPatient?: (patient: any) => void;
}

export function NurseDashboard({ onNavigate, onSelectPatient }: NurseDashboardProps) {
  const { user, loading: authLoading } = useAuth();

  // ✅ FIX: ALL HOOKS MUST BE AT THE TOP - BEFORE ANY CONDITIONAL RETURNS!
  // Fetch dashboard data from API
  const { data: dashboardData, isLoading, error, refetch } = useNurseDashboard(true);
  
  const [selectedTask, setSelectedTask] = useState<number | null>(null);  

  // Use singular assigned_patient (only ONE patient for home nurses)
  const patient = dashboardData?.assigned_patient || null;

  // ✅ FIX: Fetch all appointments for the patient using /me/appointments
  const { data: appointmentsData } = useAppointments(
    undefined, // no filters - get all appointments
    { enabled: !authLoading }
  );

  // ✅ FIX: Moved useEffect to top - BEFORE any conditional returns
  // Pass patient to parent component for CRUD operations
  useEffect(() => {
    if (patient && onSelectPatient) {
      onSelectPatient(patient);
    }
  }, [patient, onSelectPatient]);

  // NOW safe to do conditional returns - all hooks are already called
  // Show loading state
  if (isLoading) {
    return <LoadingState message="Loading nurse dashboard..." />;
  }
  

  // Show error state
  if (error) {
    return (
      <div className="container mx-auto p-6">
        <AlertCard
          type="error"
          title="Failed to load dashboard"
          description={error?.message || "An error occurred while loading the dashboard"}
          action={
            <Button onClick={() => refetch()} variant="outline">
              Try Again
            </Button>
          }
        />
      </div>
    );
  }
  
  // Show empty state if no data
  if (!dashboardData) {
    return (
      <div className="min-h-screen bg-[#D4F0F8] flex items-center justify-center p-6">
        <Card className="max-w-md w-full shadow-lg">
          <CardHeader>
            <CardTitle className="text-primary text-center">No Dashboard Data</CardTitle>
          </CardHeader>
          <CardContent>
            <EmptyState 
              title="No dashboard data available" 
              description="Please contact support if this issue persists"
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show empty state if no assigned patient
  if (!patient) {
    return (
      <div className="min-h-screen bg-[#D4F0F8] flex items-center justify-center p-6">
        <Card className="max-w-md w-full shadow-lg">
          <CardHeader>
            <CardTitle className="text-primary text-center">No Patient Assigned</CardTitle>
          </CardHeader>
          <CardContent>
            <EmptyState
              title="You don't have any patient assigned yet"
              description="Contact your coordinator to get a patient assignment. Once assigned, you'll be able to manage their care and track their progress."
              icon={User}
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Use API data with null safety
  const todayTasks = dashboardData?.today_tasks || [];
  const stats = dashboardData?.stats || {};
  const vitalsHistory = dashboardData?.vitals_history || [];
  const visitSchedule = dashboardData?.visit_schedule || [];
  const activeReminders = dashboardData?.active_reminders || [];
  
  // Fallback for missing properties from backend
  const recentActivities = patient?.recent_activities || [];
  const medications = patient?.medications || [];
  const allergies = patient?.allergies || [];

  const completedTasks = stats?.today_tasks_completed || 0;
  const totalTasks = stats?.today_tasks_total || 0;
  const tasksProgress = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;

  // ✅ FIX: Adherence Data from API - map from actual backend response
  const medicationAdherence = {
    percentage: dashboardData?.adherence?.medication?.percentage || 0,
    takenToday: dashboardData?.adherence?.medication?.takenToday || 0,
    totalToday: dashboardData?.adherence?.medication?.totalToday || 0,
    status: dashboardData?.adherence?.medication?.status || 
            ((dashboardData?.adherence?.medication?.percentage || 0) >= 90 ? 'excellent' as const :
             (dashboardData?.adherence?.medication?.percentage || 0) >= 75 ? 'good' as const : 
             'poor' as const),
    tracking: '7-day tracking' // From design
  };

  const handleTaskClick = (task: any) => {
    // Set patient context
    onSelectPatient?.(patient);
    
    // Navigate based on task type
    if (task.type === 'vitals') {
      onNavigate('nurse-vitals');
    } else if (task.type === 'spirometry' || task.type === 'hometest') {
      onNavigate('hometest');
    } else if (task.type === 'lab') {
      onNavigate('nurse-lab-sample');
    } else if (task.type === 'status') {
      onNavigate('nurse-status');
    } else if (task.type === 'visit') {
      onNavigate('appointments');
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'bg-destructive text-destructive-foreground';
      case 'medium':
        return 'bg-[#F07D13] text-white';
      case 'low':
        return 'bg-[#14CAF4] text-white';
      default:
        return 'bg-secondary';
    }
  };

  const getPriorityLabel = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'High Priority';
      case 'medium':
        return 'Medium Priority';
      case 'low':
        return 'Low Priority';
      default:
        return priority;
    }
  };

  const getTaskIcon = (type: string) => {
    switch (type) {
      case 'vitals':
        return <Stethoscope className="w-5 h-5" />;
      case 'spirometry':
      case 'hometest':
        return <ClipboardCheck className="w-5 h-5" />;
      case 'lab':
        return <TestTube className="w-5 h-5" />;
      case 'status':
        return <FileText className="w-5 h-5" />;
      case 'visit':
        return <Home className="w-5 h-5" />;
      default:
        return <Activity className="w-5 h-5" />;
    }
  };

  // Calculate last visit text
  const getLastVisitText = () => {
    if (vitalsHistory.length > 0) {
      const lastVital = vitalsHistory[vitalsHistory.length - 1];
      const lastVisitDate = new Date(lastVital.date);
      const today = new Date();
      const diffDays = Math.floor((today.getTime() - lastVisitDate.getTime()) / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      return `${diffDays} days ago`;
    }
    return 'N/A';
  };

  const getAdherenceColor = (adherence: number) => {
    if (adherence >= 90) return '#14CAF4';
    if (adherence >= 75) return '#F07D13';
    return '#EF4444';
  };

  const getAdherenceStatus = (adherence: number) => {
    if (adherence >= 90) return { label: 'Excellent', risk: 'Low Risk', icon: CheckCircle };
    if (adherence >= 75) return { label: 'Good', risk: 'Moderate Risk', icon: AlertTriangle };
    return { label: 'Poor', risk: 'High Risk', icon: AlertTriangle };
  };

  const handleLogMedication = () => {
    toast.info('Opening medication tracker...');
    onNavigate?.('medication-log');
  };

  // Calculate next visit text
  const getNextVisitText = () => {
    const todayVisit = visitSchedule.find((v: any) => v.is_today);
    if (todayVisit) return 'Today';
    
    if (visitSchedule.length > 0) {
      const nextVisit = visitSchedule[0];
      const visitDate = new Date(nextVisit.date);
      const today = new Date();
      const diffDays = Math.floor((visitDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Tomorrow';
      return `In ${diffDays} days`;
    }
    return 'Not scheduled';
  };

  return (
    <div className="min-h-screen bg-[#D4F0F8] pb-20">
      {/* Header */}
      <div className="bg-primary p-6 rounded-b-3xl shadow-md">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-primary-foreground">Home Nurse Care</h1>
            <p className="text-primary-foreground/80">Patient Care & Data Collection</p>
          </div>
          <Button
            onClick={() => onNavigate('messaging')}
            className="bg-[#14CAF4] hover:bg-[#14CAF4]/90 text-white rounded-full"
            size="icon"
          >
            <MessageCircle className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 md:px-6 lg:px-8 py-6 space-y-4">
        {/* Patient Profile Card */}
        <Card className="shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              {/* Avatar */}
              <Avatar className="w-16 h-16">
                <AvatarFallback className="bg-primary text-primary-foreground text-xl">
                  {patient.name?.charAt(0) || 'P'}
                </AvatarFallback>
              </Avatar>

              {/* Patient Info */}
              <div className="flex-1">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h2 className="text-primary">{patient.name}</h2>
                    <p className="text-sm text-muted-foreground">
                      {patient.age} years • {patient.gender} • UMR: {patient.umr_no}
                    </p>
                  </div>
                  {patient.status === 'RED_FLAG' && (
                    <Badge className="bg-destructive text-destructive-foreground">
                      Red Flag Immediate
                    </Badge>
                  )}
                </div>

                {/* Contact Info Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                  {/* Patient Phone */}
                  {patient.phone && (
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="w-4 h-4 text-muted-foreground" />
                      <div>
                        <p className="text-xs text-muted-foreground">Patient Phone</p>
                        <p>{patient.phone}</p>
                      </div>
                    </div>
                  )}

                  {/* Emergency Contact */}
                  {patient.emergency_contact && (
                    <div className="flex items-center gap-2 text-sm">
                      <AlertCircle className="w-4 h-4 text-destructive" />
                      <div>
                        <p className="text-xs text-muted-foreground">Emergency Contact</p>
                        <p className="text-destructive">{patient.emergency_contact_name || 'Contact'}</p>
                        <p className="text-destructive">{patient.emergency_contact}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Address */}
                {patient.address && (
                  <div className="flex items-start gap-2 text-sm mt-3">
                    <MapPin className="w-4 h-4 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="text-xs text-muted-foreground">Address</p>
                      <p>{patient.address}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Care Team */}
            <div className="mt-6 pt-6 border-t">
              <h3 className="text-primary text-sm mb-3">Care Team</h3>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <p className="text-xs text-muted-foreground">Doctor</p>
                  <p className="text-sm">{patient.doctor_name || 'Not assigned'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Physiotherapist</p>
                  <p className="text-sm">{patient.therapist_name || 'Not assigned'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Coordinator</p>
                  <p className="text-sm">{patient.coordinator_name || 'Not assigned'}</p>
                </div>
              </div>
            </div>

            {/* Latest Vitals */}
            {vitalsHistory.length > 0 && (
              <div className="mt-6 pt-6 border-t">
                <h3 className="text-primary text-sm mb-3">Latest Vitals</h3>
                <div className="grid grid-cols-4 gap-3">
                  <div>
                    <p className="text-xs text-muted-foreground">SpO₂</p>
                    <p className="text-lg">{vitalsHistory[vitalsHistory.length - 1].spo2 || '--'}%</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Heart Rate</p>
                    <p className="text-lg">{vitalsHistory[vitalsHistory.length - 1].heart_rate || '--'} bpm</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">BP</p>
                    <p className="text-lg">
                      {vitalsHistory[vitalsHistory.length - 1].systolic_bp && vitalsHistory[vitalsHistory.length - 1].diastolic_bp
                        ? `${vitalsHistory[vitalsHistory.length - 1].systolic_bp}/${vitalsHistory[vitalsHistory.length - 1].diastolic_bp}`
                        : '--'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Temp</p>
                    <p className="text-lg">{vitalsHistory[vitalsHistory.length - 1].temperature || '--'}°F</p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>


        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 " >
          {/* Appointments Section */}
          <div >
            <Card className="shadow-lg  h-full flex flex-col">
              <CardHeader>
                <CardTitle className="text-primary flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Upcoming Appointments
                </CardTitle>
              </CardHeader>
              <CardContent>
                {(() => {
                  // Filter upcoming appointments (not cancelled, scheduled date >= today)
                  const today = new Date();
                  
                  const upcomingAppointments = (appointmentsData || [])
                    .filter(apt => {
                      if (!apt?.scheduled_date) return false;
                      const aptDate = new Date(apt.scheduled_date);
                      return aptDate >= today && apt?.status !== 'Cancelled';
                    })
                    .sort((a, b) => new Date(a?.scheduled_date || 0).getTime() - new Date(b?.scheduled_date || 0).getTime())
                    .slice(0, 5); // Show up to 5 upcoming appointments
                  
                  if (upcomingAppointments.length === 0) {
                    return (
                      <div className="text-center py-6">
                        <Calendar className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                        <p className="text-muted-foreground">No upcoming appointments</p>
                        <Button 
                          variant="link" 
                          className="text-primary mt-2"
                          onClick={() => onNavigate?.('appointments')}
                        >
                          View All Appointments
                        </Button>
                      </div>
                    );
                  }

                  return (
                    <div className="space-y-3">
                      {upcomingAppointments.map((appointment, idx) => {
                        const aptDate = new Date(appointment.scheduled_date);
                        const formattedDate = aptDate.toLocaleDateString('en-US', { 
                          month: 'short', 
                          day: 'numeric', 
                          year: 'numeric' 
                        });
                        const formattedTime = aptDate.toLocaleTimeString('en-US', { 
                          hour: 'numeric', 
                          minute: '2-digit',
                          hour12: true 
                        });
                        
                        return (
                          <div 
                            key={appointment?.id || idx}
                            className="p-3 bg-secondary rounded-lg border border-border hover:border-primary transition-colors"
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex-1">
                                <p className="font-medium text-foreground">{appointment?.doctor_name || 'Doctor'}</p>
                                <p className="text-sm text-muted-foreground">{appointment?.appointment_type || 'Appointment'}</p>
                              </div>
                              <Badge 
                                variant={appointment?.status === 'Scheduled' ? 'default' : 'secondary'}
                                className="ml-2"
                              >
                                {appointment?.status || 'Scheduled'}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-primary">
                              <Clock className="w-4 h-4" />
                              <span>{formattedDate} at {formattedTime}</span>
                            </div>
                            {appointment?.reason && (
                              <p className="text-sm text-muted-foreground mt-1">{appointment.reason}</p>
                            )}
                          </div>
                        );
                      })}
                      <Button 
                        variant="link" 
                        className="w-full mt-2"
                        onClick={() => onNavigate?.('appointments')}
                      >
                        View All Appointments
                      </Button>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          </div>

          {/* Overview Cards Grid */}
          <div >
            <Card className="shadow-lg   h-full flex flex-col">
              <CardHeader>
                <CardTitle className="text-primary flex items-center gap-2">
                  <Stethoscope className="w-5 h-5 text-[#F07D13]" />
                  Patient Data Management
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Button
                    onClick={() => onNavigate('nurse-task-manage')}
                    className="bg-[#14CAF4] hover:bg-[#14CAF4]/90 text-white justify-start h-auto py-4 md:col-span-2"
                  >
                    <div className="flex items-center gap-3 w-full">
                      <ClipboardCheck className="w-5 h-5" />
                      <div className="text-left">
                        <p className="font-medium">Task Management</p>
                        <p className="text-xs opacity-90">Record vitals, chest drain, nutrition, tracheostomy & more</p>
                      </div>
                    </div>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Adherence Tracking Section */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-primary font-semibold flex items-center gap-2">
              <Activity className="w-5 h-5" />
              Adherence Tracking
            </h2>
          </div>
          {/* Medication Adherence Strip */}
          <Card 
            className="shadow-sm border-l-4 cursor-pointer hover:shadow-md transition-shadow" 
            style={{ borderLeftColor: getAdherenceColor(medicationAdherence.percentage) }}
            onClick={handleLogMedication}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Pill className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="font-medium">Medication Adherence</h4>
                      <div className="flex items-center gap-2">
                        <span 
                          className="text-lg font-semibold"
                          style={{ color: getAdherenceColor(medicationAdherence.percentage) }}
                        >
                          {medicationAdherence.percentage}%
                        </span>
                        <Badge 
                          style={{ 
                            backgroundColor: getAdherenceColor(medicationAdherence.percentage),
                            color: '#fff'
                          }}
                          className="text-xs"
                        >
                          {getAdherenceStatus(medicationAdherence.percentage).label}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-sm text-muted-foreground mb-2">
                      <span>Today: {medicationAdherence.takenToday}/{medicationAdherence.totalToday} doses</span>
                      <span>7-day tracking</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div 
                        className="h-1.5 rounded-full transition-all"
                        style={{ 
                          width: `${medicationAdherence.percentage}%`,
                          backgroundColor: getAdherenceColor(medicationAdherence.percentage)
                        }}
                      />
                    </div>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground ml-2" />
              </div>
            </CardContent>
          </Card>
        </div>        

        {/* Active Reminders */}
        {activeReminders.length > 0 && (
          <Card className="shadow-lg border-[#14CAF4]">
            <CardHeader>
              <CardTitle className="text-primary flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-[#14CAF4]" />
                Active Reminders
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {activeReminders.map((reminder: any, index: number) => (
                <div key={index} className="flex items-center gap-2 text-sm">
                  <div className="w-2 h-2 rounded-full bg-[#14CAF4]" />
                  <p>{reminder.message}</p>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Recent Activities */}
        {recentActivities.length > 0 && (
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="text-primary">Recent Activities</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {recentActivities.map((activity: any, index: number) => (
                <div key={index} className="flex items-center gap-3 text-sm">
                  <Activity className="w-4 h-4 text-primary flex-shrink-0" />
                  <div className="flex-1">
                    <p>{activity.description}</p>
                    <p className="text-xs text-muted-foreground">{activity.timestamp}</p>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}